import styles from '@/styles/Product.module.scss'
import BaseLayout from '@/layout/BaseLayout/BaseLayout'
import Head from 'next/head'
import React, { useMemo, useState } from 'react'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import { getArticle, getRunningQueriesThunk, useGetArticleQuery } from '@/api/article'
import { useTranslation } from 'next-i18next'
import Loading from '@/components/common/Loading/Loading'
import { getPageTitle, nl2br } from '@/utils/helpers'
import { ArticleModel } from '@/types/api'
import { DeviceType, useDeviceType } from '@/utils/hooks/useDeviceType'
import usePrice from '@/utils/hooks/usePrice'
import ProductProperty from '@/components/common/ProductProperty/ProductProperty'
import NoiseLevelIcon from '@/assets/svg/noise-level.svg'
import NoiseClassIcon from '@/assets/svg/noise-class.svg'
import FuelIcon from '@/assets/svg/fuel.svg'
import RainIcon from '@/assets/svg/rain.svg'
import WinterIcon from '@/assets/svg/winter.svg'
import ThreePMSF from '@/assets/svg/threePMSF.svg'
import classNames from 'classnames'
import ProductLabelProperty from '@/components/common/ProductLabelProperty/ProductLabelProperty'
import Button from '@/components/buttons/Button/Button'
import CartIcon from '@/assets/svg/cart.svg'
import ArticleImages from '@/components/article/ArticleImages/ArticleImages'
import Raw from '@/components/common/Raw/Raw'
import StyledPicker from '@/components/inputs/StyledPicker/StyledPicker'
import useAddToCart from '@/utils/hooks/useAddToCart'
import Banner from '@/components/common/Banner/Banner'
import StockStatus from '@/components/article/StockStatus/StockStatus'
import GpsrInfo from '@/components/article/GpsrInfo/GpsrInfo'
import { ParsedUrlQuery } from 'querystring'
import { wrapper } from '@/store/store'
import useAnalyticsManager from '@/utils/hooks/useAnalyticsManager'
import useTriggerEffect from '@/utils/hooks/useTriggerEffect'
import ProductJsonLd from '@/components/seo/ProductJsonLd'
import { useStock, StockStatus as StockStatusEnum } from '@/utils/hooks/useStock'
import { selectCountry, selectIncludeVat } from '@/features/app/appSlice'
import { useAppSelector } from '@/utils/hooks/useAppSelector'
import { resolveCountryFromRequest } from '@/utils/cookies'

type Props = {}

interface PageQueryParams extends ParsedUrlQuery {
  product: string;
  ktype: string;
  licenseplate: string;
}

const ProductName = (props: ArticleModel) => {
  return (
    <div className={styles.productName}>
      <h1 className={styles.brand}>
        {props.brandName}
      </h1>
      <h2 className={styles.model}>
        {props.articleText}
      </h2>
    </div>
  )
}

const Product = (props: Props) => {
  const { t, i18n } = useTranslation(['shop', 'common'])
  const router = useRouter()
  const deviceType = useDeviceType()
  const { product, ktype, licenseplate } = router.query as PageQueryParams
  const [quantity, setQuantity] = useState<string>('4')
  const { addToCart } = useAddToCart()
  const analyticsManager = useAnalyticsManager()
  const countryCode = useAppSelector(selectCountry)
  const includeVat = useAppSelector(selectIncludeVat)

  const productId = product ? parseInt(product[1]) : undefined
  const kTypeId = ktype ? parseInt(ktype) : undefined

  const { data, isFetching } = useGetArticleQuery({
    id: productId!,
    kType: kTypeId,
    languageCode: i18n.language,
    countryCode,
  }, { skip: !productId })

  const { formatPrice: price } = usePrice({
    currencyCode: data?.currencyCode,
  })

  const { stockStatus } = useStock(data)

  const isDisabled = stockStatus === StockStatusEnum.OutOfStock || (data?.price ?? 0) === 0

  const properties = [
    { label: t('width'), value: data?.width },
    { label: t('aspectRatio'), value: data?.articleDetails?.aspectRatio },
    { label: t('diameter'), value: data?.diameter },
    { label: t('offset'), value: data?.offset },
    { label: t('boltPattern'), value: data?.articleDetails?.boltCircle },
    { label: t('centerBore'), value: data?.articleDetails?.centerBore },
    { label: t('maxLoad'), value: data?.articleDetails?.loadRating ? `${data?.articleDetails.loadRating} kg` : null },
  ].filter((property) => property.value !== undefined && property.value !== null)

  const productLabelProperties = [
    { label: t('fuelEfficiency'), value: data?.fuelEfficiency, icon: FuelIcon },
    { label: t('wetGrip'), value: data?.wetGrip, icon: RainIcon },
    { label: t('noiseLevel'), value: data?.noiceValue, icon: NoiseLevelIcon },
    { label: t('noiseClass'), value: data?.noiceClass, icon: NoiseClassIcon },
    { label: t('winterApproved'), value: data?.isWinterApproved ? '' : null, icon: WinterIcon },
  ].filter((property) => property.value !== undefined && property.value !== null)

  const productEULabelProperties = [
    { label: t('threePmsf'), value: data?.threePmsf  ? '' : null, icon: ThreePMSF },
  ].filter((property) => property.value !== undefined && property.value !== null)

  // loop troigh 1 to 100 and add that to an array as an object for the picker
  const quantityOptions = useMemo(() => {
    const options = []
    for (let i = 1; i <= 100; i++) {
      options.push({ name: i.toString(), value: i.toString() })
    }
    return options
  }, [])


  const onAddToCart = () => {
    if (!quantity || !productId)
      return

    addToCart({
      articleId: productId,
      quantity: parseInt(quantity),
      kType: kTypeId,
      licensePlate: licenseplate,
      articleText: data?.articleText || '',
      price: data?.price || 0,
    })
  }

  const onQuantityChange = (value: string | null) => {
    if (!value)
      return

    setQuantity(value)
  }

  useTriggerEffect(() => {
    if (!data) return
    analyticsManager.viewProduct(data)
  }, [analyticsManager, data], data !== undefined && !isFetching)

  const canonicalUrl = data?.productLink
    ? `${process.env.NEXT_PUBLIC_HOST}/${data.productLink}`
    : undefined

  const ogImageUrl = data?.imageId
    ? `${process.env.NEXT_PUBLIC_API}/images/${data.imageId}`
    : undefined

  return (
    <BaseLayout>
      <Head>
        <title>{getPageTitle(data?.articleText || '')}</title>
        {data && (
          <>
            <meta property="og:title" content={`${data.brandName} ${data.articleText}`} />
            <meta property="og:description" content={data.articleDetails?.description || data.articleText} />
            <meta property="og:type" content="product" />
            {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
            {ogImageUrl && <meta property="og:image" content={ogImageUrl} />}
            <meta property="product:price:amount" content={data.price.toString()} />
            <meta property="product:price:currency" content={data.currencyCode} />
          </>
        )}
        {data && canonicalUrl && <ProductJsonLd article={data} url={canonicalUrl} />}
      </Head>
      <main className={styles.Product}>
        {isFetching && <Loading />}
        {data && (
          <article className={styles.productContainer}>
            {data.isNew && (
              <div className={styles.banner}>
                <Banner
                  text={t('common:search.news')}
                />
              </div>
            )}
            {deviceType === DeviceType.Mobile && (
              <div>
                <ProductName {...data} />
              </div>
            )}
            <section className={classNames(styles.images, { [styles.hasImage]: !!data.imageId })}>
              {data.imageId && (
                <ArticleImages
                  imageId={data.imageId}
                  images={data.articleDetails?.images}
                  alt={data.brandName}
                />
              )}
            </section>
            <section>
              {deviceType === DeviceType.Desktop && (
                <ProductName {...data} />
              )}
              <div className={styles.section}>
                <div className={styles.price}>
                  <div className={styles.currentPrice}>
                    {price(includeVat ? data.price : data.priceExVat, true)}
                  </div>
                  {data.regularPrice && (
                    <div className={styles.regularPrice}>
                      {t('regularPrice')} {price(includeVat ? data.regularPrice : data.regularPriceExVat, true)}
                    </div>
                  )}
                </div>
                <ProductProperty
                  label={t('artNumber')}
                  value={data.id}
                />
              </div>
              {properties.length > 0 && (
                <div className={classNames(styles.section, styles.properties)}>
                  {
                    properties.map((property) => (
                      <ProductProperty
                        key={property.label}
                        label={property.label}
                        value={property.value}
                        bigValue={true}
                      />
                    ))
                  }
                </div>
              )}
              {productLabelProperties.length > 0 && (
                <div className={classNames(styles.section, styles.iconProperties)}>
                  {productLabelProperties.map((property) => (
                    <ProductLabelProperty
                      key={property.label}
                      label={property.label}
                      value={property.value || ''}
                      icon={property.icon}
                    />
                  ))}
                </div>
              )}
              {productEULabelProperties.length > 0 && (
                <div className={classNames(styles.section, styles.iconProperties)}>
                  {productEULabelProperties.map((property) => (
                    <ProductLabelProperty
                      key={property.label}
                      label={property.label}
                      value={property.value || ''}
                      icon={property.icon}
                    />
                  ))}
                </div>
              )}
              <div className={styles.productDescription}>
                <div className={styles.description}>
                  {data?.articleDetails?.description?.length > 0 && (
                    <>
                      <h3 className={styles.productDescriptionHeader}>{t('productDescription')}</h3>
                      <Raw content={nl2br(data.articleDetails.description)} />
                    </>
                  )}
                  {data?.gpsr && (
                    <GpsrInfo gpsr={data.gpsr} title={t('gpsr')} />
                  )}
                </div>
                <div className={styles.addToCart}>
                  <div className={styles.stockStatus}>
                    <label>{t('stockStatus')}</label>
                    <StockStatus article={data} />
                  </div>
                  <StyledPicker
                    name={'width'}
                    placeholder={t('shop.selectWidth')}
                    options={quantityOptions}
                    light={true}
                    value={quantity}
                    onChange={onQuantityChange}
                    disabled={isDisabled}
                  />
                  <Button
                    rounded={true}
                    medium={true}
                    label={t('addToCart')}
                    onClick={onAddToCart}
                    icon={CartIcon}
                    iconPosition="right"
                    disabled={isDisabled}
                  />
                </div>
              </div>
            </section>
          </article>
        )}
      </main>
    </BaseLayout>
  )
}

export default Product

export const getServerSideProps = wrapper.getServerSideProps(
  (store) => async (context) => {
    const { product, ktype } = context.query as PageQueryParams
    const productId = product && parseInt(product[1])
    const kTypeId = ktype ? parseInt(ktype) : undefined
    const countryCode = resolveCountryFromRequest(context)

    if (!productId || isNaN(productId)) {
      return { notFound: true }
    }

    const articleArgs = {
      id: productId,
      languageCode: context.locale || 'sv',
      countryCode,
      ...(kTypeId != null && { kType: kTypeId }),
    }

    store.dispatch(getArticle.initiate(articleArgs))
    await Promise.all(store.dispatch(getRunningQueriesThunk()))

    const state = store.getState()
    const result = getArticle.select(articleArgs)(state)

    if (!result.data) {
      return { notFound: true }
    }

    const translations = await serverSideTranslations(context.locale || 'sv', [
      'common',
      'shop',
      'footer',
    ])

    return {
      props: {
        ...translations,
      },
    }
  }
)